import {defineMessage} from '../../../libs/shared/core/src/helpers/internationalization/message';
import {UnreachableError} from '../../../libs/shared/core/src/helpers/types/customErrors';
import {isDefined} from '../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {collectEnumValues, makeEnumTypeGuard} from '../../../libs/shared/core/src/models/api/types/enumTypes';

/*
 * Types.
 */

export enum ModifiersEnum {
  ACCOUNT = 'account',
  AFTER = 'after',
  ASSIGNEE = 'assignee',
  AUTHOR = 'author',
  BCC = 'bcc',
  BEFORE = 'before',
  BODY = 'body',
  CC = 'cc',
  CHANNEL_TYPE = 'channel_type',
  COMMENTER = 'commenter',
  DURING = 'during',
  FROM = 'from',
  HAS = 'has',
  ID = 'id',
  IN = 'in',
  INBOX = 'inbox',
  INBOXES = 'inboxes',
  IS = 'is',
  MENTION = 'mention',
  OBJECT = 'object',
  PARTICIPANT = 'participant',
  RECIPIENT = 'recipient',
  SUBJECT = 'subject',
  TAG = 'tag',
  CUSTOM_FIELD = 'custom_field',
  TO = 'to',
  TICKET_ID = 'ticket_id',
  TYPE = 'type',
  LINK = 'link',
  WITH = 'with',
}

export type ContactModifiers = ModifiersEnum.FROM | ModifiersEnum.TO | ModifiersEnum.WITH;

export function contactModifierTitleDescriptor(contactModifier: ContactModifiers) {
  switch (contactModifier) {
    case ModifiersEnum.FROM:
      return defineMessage({
        id: `search-modifier-title-from`,
        message: 'From',
      });

    case ModifiersEnum.TO:
      return defineMessage({
        id: `search-modifier-title-to`,
        message: 'To',
      });

    case ModifiersEnum.WITH:
      return defineMessage({
        id: `search-modifier-title-with`,
        message: 'With',
      });

    default:
      throw new UnreachableError(contactModifier);
  }
}

export const modifiersSet = new Set(collectEnumValues(ModifiersEnum));

const validTypeModifierValues = [
  'call',
  'custom',
  'discussion',
  'email',
  'facebook',
  'front_chat',
  'googleplay',
  'intercom',
  'internal',
  'layer',
  'phone-call',
  'smooch',
  'sms',
  'tweet_dm',
  'tweet',
  'whatsapp',
  'yalo_wha',
];

const validModifierValues = new Map(
  Object.entries({
    [ModifiersEnum.IS]: [
      'archived',
      'assigned',
      'discussion',
      'open',
      'shared-with-me',
      'snoozed',
      'spam',
      'starred',
      'subscribed',
      'trashed',
      'unassigned',
      'unread',
      'unreplied',
      'unsubscribed',
      'waiting',
      'resolved',
    ],
    [ModifiersEnum.HAS]: [
      'attachment',
      'attachments',
      'calendar',
      'draft',
      'follower',
      'followers',
      'no-calendar',
      'no-tag',
      'no-tags',
      'reminder',
      'subscriber',
      'subscribers',
    ],
    [ModifiersEnum.IN]: ['all', 'attachments', 'comments', 'messages'],
    [ModifiersEnum.TYPE]: validTypeModifierValues,
    [ModifiersEnum.CHANNEL_TYPE]: validTypeModifierValues,
  }),
);

/*
 * Type guards.
 */

export const isExistingModifier = (
  modifier: string,
  hasTicketing = false,
): // TODO(khaled): Remove eslint-disable when we remove the ticketing modifier.
// eslint-disable-next-line
modifier is ModifiersEnum => {
  if (hasTicketing) {
    return makeEnumTypeGuard(ModifiersEnum)(modifier);
  }
  return makeEnumTypeGuard(ModifiersEnum)(modifier) && modifier !== ModifiersEnum.TICKET_ID;
};

/*
 * Helpers.
 */

export function isValidValueForModifier(modifier: ModifiersEnum, value: string | undefined): boolean {
  if (
    isDefined(value) &&
    validModifierValues.has(modifier) &&
    !(validModifierValues.get(modifier) || []).includes(value.trim())
  ) {
    return false;
  }

  return true;
}

export const TRANSIENT_MODIFIERS = [
  ModifiersEnum.FROM,
  ModifiersEnum.TO,
  ModifiersEnum.CC,
  ModifiersEnum.BCC,
];
